import React, { useCallback, useEffect, useState } from 'react'
import { Card } from 'primereact/card'
import { FiPlusCircle } from 'react-icons/fi'
import { HiOutlineDocumentReport } from 'react-icons/hi'
import { FaCheckDouble, FaTrash } from 'react-icons/fa'
import toast from 'react-hot-toast'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'

import NoDataFound from '../../../../shared/NoDataFound'
import SkeletonCardLoading from '../../../../shared/SkeltonCardLoading'
import DropdownField from '../../../../shared/DropdownField'
import TextInput from '../../../../shared/TextInput'
import CustomButton from '../../../../shared/CustomButton'
import { fetchData } from '../../../../helpers/fetchData'
import {
  filterFormsByRequestType,
  labModuleApi
} from '../../../../helpers/labModuleApi'
import FormListItem from '../../form-creations/FormListItem'
import { formListTypes } from '../../../../constants/FormCreationConstants'
import CheckPermission from '../../../../hooks/CheckPermission'
const ConfirmationModal = React.lazy(() =>
  import('../../../../shared/ConfirmationModal')
)
const ReusableModal = React.lazy(() =>
  import('../../../../shared/ReuseableModal')
)

const IPFormsList = ({
  selectedFormType,
  setSelectedFormId,
  isAllowActions = true
}) => {
  const { hasPermission } = CheckPermission()
  const [isLoading, setIsLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [formsData, setFormsData] = useState([])
  const [selectedForm, setSelectedForm] = useState(null)
  const [openCreateFormModal, setOpenCreateFormModal] = useState(false)
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)
  const [activeItemId, setActiveItemId] = useState(null)

  const fetchReports = useCallback(async () => {
    setIsLoading(true)
    const response = await fetchData('GET', labModuleApi.forms.getAll())
    let formsData = filterFormsByRequestType(
      response?.data || [],
      selectedFormType
    )
    setFormsData(formsData)
    setIsLoading(false)
  }, [selectedFormType])

  useEffect(() => {
    fetchReports()
  }, [selectedFormType])

  const handleFormItemClick = data => {
    setActiveItemId(data?.id)
    setSelectedFormId(data?.id)
  }

  const handleAddNewReport = () => {
    setSelectedForm(null)
    setOpenCreateFormModal(true)
  }

  const handleEditForm = formData => {
    setSelectedForm(formData)
    setOpenCreateFormModal(true)
  }
  const hanldeDeleteForm = formData => {
    setSelectedForm(formData)
    setOpenConfirmationModal(true)
  }

  const handleConfirmDeleteForm = async () => {
    if (!hasPermission('forms-creation', 'delete')) return
    setButtonLoading(true)
    let response = await fetchData(
      'DELETE',
      labModuleApi.forms.remove(selectedForm?.id)
    )
    if (response) {
      toast.success(response?.message)
      setSelectedForm(null)
      setOpenConfirmationModal(false)
      fetchReports()
    }
    setButtonLoading(false)
  }

  return (
    <Card
      pt={{
        root: { className: 'border border-gray-100 shadow px-0 h-fit' },
        body: { className: 'px-0' }
      }}
    >
      <div className='flex items-center justify-between px-3 border-b border-gray-300 pb-3'>
        <h6 className='text-20-gray !font-semibold'>Forms</h6>
        {isAllowActions && hasPermission('forms-creation', 'create') && (
          <div>
            <FiPlusCircle
              size={27}
              className='text-blue cursor-pointer'
              onClick={handleAddNewReport}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        <SkeletonCardLoading count={10} />
      ) : !formsData?.length ? (
        <div className='my-4 lg:my-8'>
          <NoDataFound
            iconSize='30'
            titleClassName='text-16-dark font-semibold'
          />
        </div>
      ) : (
        <div className='max-h-[400px] lg:max-[500px] xl:max-h-[600px] overflow-y-auto'>
          {formsData?.map((data, index) => (
            <div
              key={index}
              className={`py-3 px-3 border-b border-b-gray-100 ${
                activeItemId == data?.id ? 'bg-gray-200' : ''
              }`}
            >
              <FormListItem
                isAllowActions={isAllowActions}
                data={data}
                onEditClick={handleEditForm}
                onDeleteClick={hanldeDeleteForm}
                onContainerClick={handleFormItemClick}
              />
            </div>
          ))}
        </div>
      )}
      <ReusableModal
        isVisible={openCreateFormModal}
        position='top'
        title={selectedForm ? 'Update Form' : 'Create New Form'}
        HeaderIcon={HiOutlineDocumentReport}
        onClose={() => {
          setOpenCreateFormModal(false)
          setSelectedForm(null)
        }}
      >
        <CreateReportForm
          selectedForm={selectedForm}
          selectedFormType={selectedFormType}
          fetchReports={fetchReports}
          setOpenCreateFormModal={setOpenCreateFormModal}
          setSelectedForm={setSelectedForm}
        />
      </ReusableModal>
      <ConfirmationModal
        disabled={buttonLoading}
        isLoading={buttonLoading}
        visible={openConfirmationModal}
        setVisible={setOpenConfirmationModal}
        header={'Confirm Delete'}
        acceptLabel={'Delete'}
        rejectLabel={'Cancel'}
        Icon={FaTrash}
        onAccept={() => handleConfirmDeleteForm()}
        onReject={() => setSelectedForm(null)}
      />
    </Card>
  )
}

const CreateReportForm = ({
  selectedForm,
  selectedFormType,
  fetchReports,
  setOpenCreateFormModal,
  setSelectedForm
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const initialValues = {
    type: selectedForm?.type || '',
    name_en: selectedForm?.name_en || '',
    name_ar: selectedForm?.name_ar || '',
    request_type: selectedFormType || 'visit'
  }

  const validationSchema = Yup.object().shape({
    type: Yup.string().trim().required('Type is required'),
    name_en: Yup.string().trim().required('Report Name (EN) is required'),
    name_ar: Yup.string().trim().required('Report Name (AR) is required')
  })

  const handleSubmit = async values => {
    selectedForm ? handleUpdateForm(values) : handleCreateForm(values)
  }

  const handleCreateForm = async payload => {
    setIsLoading(true)
    let response = await fetchData('POST', labModuleApi.forms.create(), payload)
    setIsLoading(false)
    if (response) {
      toast.success(response?.message)
      fetchReports()
      setOpenCreateFormModal(false)
      setSelectedForm(null)
    }
  }

  const handleUpdateForm = async payload => {
    setIsLoading(true)
    let response = await fetchData(
      'PUT',
      labModuleApi.forms.update(selectedForm?.id),
      payload
    )
    setIsLoading(false)
    if (response) {
      toast.success(response?.message)
      fetchReports()
      setOpenCreateFormModal(false)
      setSelectedForm(null)
    }
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {props => (
          <Form>
            <div className='grid grid-cols-2 gap-2 mt-4'>
              <DropdownField
                name='type'
                label='Select Type*'
                options={formListTypes}
                optionLabel='label'
                optionValue='value'
                placeholder='Select report type'
              />
              <TextInput
                name='name_en'
                label='Report Name (EN)*'
                placeholder='Enter report name en'
              />
              <TextInput
                name='name_ar'
                label='Report Name (AR)'
                placeholder='Enter report name ar'
              />
            </div>
            <div className='flex items-center justify-end mt-2'>
              <CustomButton
                disabled={isLoading}
                loading={isLoading}
                type='submit'
                label={selectedForm ? 'Update Form' : 'Add Form'}
                buttonType='primary'
                icon={<FaCheckDouble size={12} className='me-2' />}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default IPFormsList
